import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAudioManifest,
  fetchTranscript,
  turnAudioUrl,
  type TranscriptCorrection,
  type TranscriptResponse,
  type TranscriptTurn,
} from '../../api/admin.js';
import { formatDurationMs, shortId } from '../../lib/format.js';
import { GATED_CONTENT_PLACEHOLDER, isGatedModuleError } from '../../lib/privacy.js';

const SHOW_CORRECTIONS_STORAGE_KEY = 'admin.sessionChat.showCorrections';
const SHOW_THREAD_STORAGE_KEY = 'admin.sessionChat.showThread';

/** Tope de ancestros a recorrer al "ver todo el hilo" (anti-loop / anti-prompt gigante). */
const MAX_THREAD_DEPTH = 12;

/**
 * Camina la cadena de `parentSessionId` desde la sesión actual hacia la raíz,
 * devolviendo los transcripts ordenados de la MÁS VIEJA a la actual. Cada hop
 * es un fetchTranscript. Acotado por MAX_THREAD_DEPTH.
 */
async function fetchThreadSegments(sessionId: string): Promise<TranscriptResponse[]> {
  const current = await fetchTranscript(sessionId);
  const segments: TranscriptResponse[] = [current];
  let parentId = current.session.parentSessionId ?? null;
  let depth = 0;
  while (parentId && depth < MAX_THREAD_DEPTH) {
    const parent = await fetchTranscript(parentId);
    segments.unshift(parent); // más vieja primero
    parentId = parent.session.parentSessionId ?? null;
    depth += 1;
  }
  return segments;
}

export interface SessionChatModalProps {
  open: boolean;
  sessionId: string;
  onClose: () => void;
}

interface ChatItem {
  key: string;
  kind: 'user' | 'assistant';
  turnIndex: number;
  text: string;
  startedAt: string;
  interrupted: boolean;
  offsetMs: number;
  /** Solo poblado en items kind='user' cuando la sesión es languages. */
  corrections: TranscriptCorrection[];
  /** Sesión a la que pertenece el turno (para marcar fronteras del hilo). */
  sessionId: string;
}

function buildChatItems(turns: readonly TranscriptTurn[], sessionId: string): ChatItem[] {
  if (turns.length === 0) return [];
  const t0Iso = turns[0]?.startedAt;
  const t0 = t0Iso ? new Date(t0Iso).getTime() : 0;
  const items: ChatItem[] = [];
  for (const turn of turns) {
    const startMs = new Date(turn.startedAt).getTime();
    const offset = Math.max(0, startMs - t0);
    if (turn.userText && turn.userText.trim().length > 0) {
      items.push({
        key: `${sessionId}-user-${turn.turnIndex}`,
        kind: 'user',
        turnIndex: turn.turnIndex,
        text: turn.userText.trim(),
        startedAt: turn.startedAt,
        interrupted: turn.interrupted,
        offsetMs: offset,
        corrections: turn.corrections,
        sessionId,
      });
    }
    if (turn.assistantText && turn.assistantText.trim().length > 0) {
      items.push({
        key: `${sessionId}-assistant-${turn.turnIndex}`,
        kind: 'assistant',
        turnIndex: turn.turnIndex,
        text: turn.assistantText.trim(),
        startedAt: turn.startedAt,
        interrupted: turn.interrupted,
        offsetMs: offset,
        corrections: [],
        sessionId,
      });
    }
  }
  return items;
}

/** Aplana varios segmentos (sesiones del hilo) a una sola lista de items. */
function buildThreadItems(segments: readonly TranscriptResponse[]): ChatItem[] {
  return segments.flatMap((seg) => buildChatItems(seg.turns, seg.session.id));
}

export function SessionChatModal({
  open,
  sessionId,
  onClose,
}: SessionChatModalProps): ReactElement | null {
  // Toggle "ver todo el hilo": cuando está ON, prepende los transcripts de las
  // sesiones padre (resume) para leer la conversación completa de corrido.
  // Persistido en localStorage. Default OFF (la mayoría de las sesiones no son
  // continuaciones; sólo sumamos contexto cuando el admin lo pide).
  const [showThread, setShowThread] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SHOW_THREAD_STORAGE_KEY) === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SHOW_THREAD_STORAGE_KEY, showThread ? '1' : '0');
  }, [showThread]);

  // query.data = segmentos del hilo (más viejo → actual). Con el toggle OFF es
  // un único segmento (la sesión actual). El último segmento es SIEMPRE la
  // sesión abierta.
  const query = useQuery({
    queryKey: ['admin', 'transcript-thread', sessionId, showThread],
    queryFn: (): Promise<TranscriptResponse[]> =>
      showThread ? fetchThreadSegments(sessionId) : fetchTranscript(sessionId).then((t) => [t]),
    enabled: open && Boolean(sessionId),
  });

  const segments = query.data ?? [];
  const currentSession = segments.length > 0 ? segments[segments.length - 1]!.session : null;
  const totalTurns = segments.reduce((acc, s) => acc + s.turns.length, 0);
  const hasParent = Boolean(currentSession?.parentSessionId);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const items = useMemo<ChatItem[]>(() => buildThreadItems(segments), [segments]);

  const totalCorrections = useMemo(
    () => items.reduce((acc, it) => acc + it.corrections.length, 0),
    [items],
  );

  // Manifest de audio por cada sesión del hilo: qué turnos tienen WAV
  // persistido (RECORD_SESSION_AUDIO). Construimos un Set de claves
  // `${sessionId}:${turnIndex}:${kind}` para mostrar el botón de play sólo en
  // las burbujas que efectivamente tienen audio grabado.
  const sessionIds = useMemo(() => segments.map((s) => s.session.id), [segments]);
  const audioQuery = useQuery({
    queryKey: ['admin', 'chat-audio-manifest', sessionIds],
    queryFn: async (): Promise<Set<string>> => {
      const results = await Promise.all(
        sessionIds.map((id) =>
          fetchAudioManifest(id).catch(() => ({ sessionId: id, items: [] })),
        ),
      );
      const keys = new Set<string>();
      for (const r of results) {
        for (const it of r.items) keys.add(`${r.sessionId}:${it.turnIndex}:${it.kind}`);
      }
      return keys;
    },
    enabled: open && sessionIds.length > 0,
  });
  const audioKeys = audioQuery.data;

  // Un único `<audio>` reusable: reproduce el turno on-demand al tocar play.
  // El endpoint es same-origin (`/api/admin/...`), así que la cookie httpOnly
  // viaja sola — no hace falta pre-fetch a Blob como en el replay.
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);

  const togglePlay = useCallback(
    (it: ChatItem): void => {
      const el = audioElRef.current;
      if (!el) return;
      if (playingKey === it.key) {
        el.pause();
        setPlayingKey(null);
        return;
      }
      el.src = turnAudioUrl(it.sessionId, it.turnIndex, it.kind);
      el.currentTime = 0;
      void el
        .play()
        .then(() => setPlayingKey(it.key))
        .catch((err) => {
          console.warn('[chat] audio play failed', err);
          setPlayingKey(null);
        });
    },
    [playingKey],
  );

  // Toggle persistido en localStorage para que el admin no tenga que
  // re-tildarlo cada vez que abre un modal distinto. Default ON: las
  // correcciones son la dimensión más cara de inspeccionar y queremos
  // verlas por default.
  const [showCorrections, setShowCorrections] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem(SHOW_CORRECTIONS_STORAGE_KEY);
    return stored == null ? true : stored === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SHOW_CORRECTIONS_STORAGE_KEY, showCorrections ? '1' : '0');
  }, [showCorrections]);

  // Limpiar el estado de reproducción y frenar el audio al cerrar el modal.
  useEffect(() => {
    if (open) return;
    const el = audioElRef.current;
    if (el) {
      el.pause();
      el.removeAttribute('src');
    }
    setPlayingKey(null);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="mm-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="session-chat-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="mm-dialog chat-dialog">
        <header className="mm-head replay-head">
          <div>
            <h3 className="mm-title">Chat de la sesión</h3>
            {currentSession ? (
              <div className="replay-sub">
                <span className="replay-sub-key">user</span> {currentSession.userEmail}
                {currentSession.moduleId ? (
                  <>
                    <span className="replay-sub-sep">·</span>
                    <span className="replay-sub-key">module</span> {currentSession.moduleId}
                  </>
                ) : null}
                <span className="replay-sub-sep">·</span>
                <span className="replay-sub-key">turns</span> {totalTurns}
                {showThread && segments.length > 1 ? (
                  <>
                    <span className="replay-sub-sep">·</span>
                    <span className="replay-sub-key">hilo</span> {segments.length} sesiones
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="chat-head-actions">
            {/* Toggle de hilo: sólo si la sesión actual es continuación de otra
                (tiene parent). Suma los turnos de las sesiones anteriores. */}
            {hasParent ? (
              <label
                className="chat-toggle"
                title="Incluir los turnos de las sesiones anteriores del hilo (resume)"
              >
                <input
                  type="checkbox"
                  checked={showThread}
                  onChange={(e) => setShowThread(e.target.checked)}
                  data-testid="session-chat-show-thread"
                />
                <span>Ver todo el hilo</span>
              </label>
            ) : null}
            {/* Mostramos el toggle si la sesión es del módulo languages
                (potencialmente tiene correcciones) o si ya hay alguna
                persistida. En módulos sin correcciones no agregamos UI
                irrelevante. Cuando count = 0 mostramos `(0)` para que el
                admin sepa que el módulo soporta corrección pero el side-runner
                no encontró errores corregibles en esta sesión. */}
            {(currentSession?.moduleId === 'languages' || totalCorrections > 0) ? (
              <label className="chat-toggle" title="Mostrar/ocultar chips de corrección bajo cada turno del user">
                <input
                  type="checkbox"
                  checked={showCorrections}
                  onChange={(e) => setShowCorrections(e.target.checked)}
                  data-testid="session-chat-show-corrections"
                />
                <span>Correcciones ({totalCorrections})</span>
              </label>
            ) : null}
            <button
              type="button"
              className="btn"
              onClick={onClose}
              data-testid="session-chat-close"
              aria-label="Cerrar chat"
            >
              ✕
            </button>
          </div>
        </header>

        <div className="mm-body chat-body">
          {query.isPending ? (
            <div className="replay-empty">Cargando transcript…</div>
          ) : query.isError ? (
            // 403 gated_module (módulo sensible): placeholder sobrio, no error.
            isGatedModuleError(query.error) ? (
              <div className="replay-empty" data-testid="session-chat-gated">
                🔒 {GATED_CONTENT_PLACEHOLDER}
              </div>
            ) : (
              <div className="mm-error" role="alert">
                {query.error instanceof Error
                  ? query.error.message
                  : 'Error al cargar el transcript'}
              </div>
            )
          ) : items.length === 0 ? (
            <div className="replay-empty">
              Esta sesión no tiene turnos con texto persistido. Tracing pudo haber estado apagado.
            </div>
          ) : (
            <div className="chat-list" data-testid="chat-list">
              {items.map((it, idx) => {
                const prev = idx > 0 ? items[idx - 1] : undefined;
                // Frontera de sesión del hilo: cuando cambia el sessionId
                // (sólo posible con "ver todo el hilo" ON). Tiene prioridad
                // sobre el separador de turno.
                const newSession = prev !== undefined && prev.sessionId !== it.sessionId;
                const isCurrent = it.sessionId === currentSession?.id;
                const showSep = !newSession && prev !== undefined && prev.turnIndex !== it.turnIndex;
                return (
                  <Fragment key={it.key}>
                    {newSession ? (
                      <div className="chat-turn-sep chat-session-sep" aria-hidden="true">
                        <span>
                          {isCurrent ? '▸ sesión actual' : '↩ sesión anterior'} ·{' '}
                          {shortId(it.sessionId, 8)}
                        </span>
                      </div>
                    ) : null}
                    {showSep ? (
                      <div className="chat-turn-sep" aria-hidden="true">
                        <span>turn {it.turnIndex}</span>
                      </div>
                    ) : null}
                    <div
                      className={`chat-bubble chat-bubble--${it.kind}${
                        it.interrupted ? ' chat-bubble--interrupted' : ''
                      }`}
                    >
                      <div className="chat-bubble-meta">
                        {audioKeys?.has(`${it.sessionId}:${it.turnIndex}:${it.kind}`) ? (
                          <button
                            type="button"
                            className={`chat-bubble-play${
                              playingKey === it.key ? ' chat-bubble-play--on' : ''
                            }`}
                            onClick={() => togglePlay(it)}
                            data-testid={`chat-play-${it.kind}-${it.turnIndex}`}
                            title={playingKey === it.key ? 'Pausar audio' : 'Escuchar audio'}
                            aria-label={playingKey === it.key ? 'Pausar audio' : 'Escuchar audio'}
                          >
                            {playingKey === it.key ? '⏸' : '▶'}
                          </button>
                        ) : null}
                        <span>{it.kind === 'user' ? 'User' : 'Assistant'}</span>
                        <span className="chat-bubble-meta-when">
                          turn {it.turnIndex} · +{formatDurationMs(it.offsetMs)}
                        </span>
                        {it.interrupted ? (
                          <span title="Turno interrumpido">interrupted</span>
                        ) : null}
                      </div>
                      <div className="chat-bubble-text">{it.text}</div>
                      {showCorrections && it.corrections.length > 0 ? (
                        <ul
                          className="chat-corrections"
                          data-testid={`chat-corrections-${it.turnIndex}`}
                        >
                          {it.corrections.map((c, ci) => (
                            <li
                              key={ci}
                              className={`chat-correction chat-correction--${c.severity}`}
                              title={c.explanation ?? undefined}
                            >
                              <span className="chat-correction-said">{c.userUtterance}</span>
                              <span className="chat-correction-arrow" aria-hidden="true">→</span>
                              <span className="chat-correction-fix">{c.correctedForm}</span>
                              {c.category ? (
                                <span className="chat-correction-cat">{c.category}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* `<audio>` reusable para escuchar el turno on-demand desde su burbuja. */}
        <audio
          ref={audioElRef}
          preload="none"
          style={{ display: 'none' }}
          onEnded={() => setPlayingKey(null)}
        />
      </div>
    </div>
  );
}
